import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scissors, Clock, Calendar, Play, Image, AlertCircle, Search, X, SlidersHorizontal, IndianRupee, ChevronDown, ChevronUp, ArrowUpDown, Filter, Timer, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_RANGES = [
  { label: 'Under ₹200', min: 0, max: 200 },
  { label: '₹200 – ₹500', min: 200, max: 500 },
  { label: '₹500 – ₹1,000', min: 500, max: 1000 },
  { label: 'Over ₹1,000', min: 1000, max: null },
];

const DURATION_RANGES = [
  { label: 'Quick (< 30 min)', min: 0, max: 30 },
  { label: 'Medium (30–60 min)', min: 30, max: 60 },
  { label: 'Long (60+ min)', min: 60, max: null },
];

const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'duration_short', label: 'Duration: Short first' },
  { value: 'duration_long', label: 'Duration: Long first' },
  { value: 'name', label: 'Name A → Z' },
];

export default function Services() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [petName, setPetName] = useState('');
  const [notes, setNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [myAppointments, setMyAppointments] = useState([]);
  const [mediaOpen, setMediaOpen] = useState(null);
  
  // Reviews state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState(null);
  const [durationRange, setDurationRange] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Collapse state
  const [priceOpen, setPriceOpen] = useState(true);
  const [durationOpen, setDurationOpen] = useState(true);
  const [sortOpen, setSortOpen] = useState(true);

  useEffect(() => { 
    axios.get(`${API}/services`).then(r => {
      setServices(r.data.services);
      setError(null);
    }).catch((err) => {
      console.error("Services fetch error:", err);
      setError(err.message || 'Failed to fetch services');
      setServices([]);
    }).finally(() => setLoading(false)); 
  }, []);
  useEffect(() => { if (user) axios.get(`${API}/appointments`, { withCredentials: true }).then(r => setMyAppointments(r.data.appointments)).catch(() => {}); }, [user]);

  // Apply filters
  const filteredServices = useMemo(() => {
    let result = [...services];

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q));
    }

    // Price range
    if (priceRange) {
      result = result.filter(s => {
        if (priceRange.max === null) return s.price >= priceRange.min;
        return s.price >= priceRange.min && s.price <= priceRange.max;
      });
    }

    // Duration range
    if (durationRange) {
      result = result.filter(s => {
        if (durationRange.max === null) return s.duration_minutes >= durationRange.min;
        return s.duration_minutes >= durationRange.min && s.duration_minutes <= durationRange.max;
      });
    }

    // Sort
    if (sortBy === 'price_low') result.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price_high') result.sort((a, b) => b.price - a.price);
    else if (sortBy === 'duration_short') result.sort((a, b) => a.duration_minutes - b.duration_minutes);
    else if (sortBy === 'duration_long') result.sort((a, b) => b.duration_minutes - a.duration_minutes);
    else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [services, searchQuery, priceRange, durationRange, sortBy]);

  const activeFilterCount = [searchQuery, priceRange, durationRange, sortBy !== 'default'].filter(Boolean).length;

  const clearAllFilters = () => {
    setSearchQuery('');
    setPriceRange(null);
    setDurationRange(null);
    setSortBy('default');
  };

  const fetchSlots = async (serviceId, date) => {
    setSelectedDate(date);
    setSlotsLoading(true);
    try {
      const { data } = await axios.get(`${API}/services/${serviceId}/slots?date=${date}`);
      setSlots(data.slots);
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  };

  const openBooking = (service) => {
    if (!user) { navigate('/login'); return; }
    setSelectedService(service);
    setBookingOpen(true);
    setSlots([]); setSelectedSlot(null); setPetName(''); setNotes('');
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    const d = tomorrow.toISOString().split('T')[0];
    fetchSlots(service.id, d);
  };

  const handleBook = async () => {
    if (!selectedSlot) { toast.error('Please select a time slot first'); return; }
    setBookingLoading(true);
    try {
      await axios.post(`${API}/appointments`, { service_id: selectedService.id, slot_id: selectedSlot.id, pet_name: petName, notes }, { withCredentials: true });
      toast.success('Appointment booked successfully!');
      setBookingOpen(false);
      const { data } = await axios.get(`${API}/appointments`, { withCredentials: true });
      setMyAppointments(data.appointments);
    } catch (err) { toast.error(err.response?.data?.detail || 'Booking failed. Please try again.'); }
    finally { setBookingLoading(false); }
  };

  const openReviewModal = async (service) => {
    setSelectedService(service);
    setReviewModalOpen(true);
    setReviewsLoading(true);
    try {
      const { data } = await axios.get(`${API}/reviews/service/${service.id}`);
      setReviews(data.reviews || []);
    } catch (err) {
      toast.error('Failed to load reviews');
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    setReviewLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews/service/${selectedService.id}`, { rating: reviewRating, comment: reviewComment }, { withCredentials: true });
      setReviews([data.review, ...reviews]);
      setReviewRating(5);
      setReviewComment('');
      toast.success('Review added successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  // ── Filter Panel ──
  const FilterPanel = () => (
    <div className="font-body" data-testid="service-filter-panel">
      {activeFilterCount > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', background: 'rgba(196,94,42,0.06)', borderRadius: '12px', marginBottom: '16px',
        }}>
          <span style={{ fontSize: '0.82rem', color: '#C45E2A', fontWeight: 600 }}>
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
          </span>
          <button onClick={clearAllFilters} style={{
            fontSize: '0.78rem', color: '#C45E2A', fontWeight: 500, cursor: 'pointer',
            background: 'none', border: 'none', textDecoration: 'underline',
          }}>Clear All</button>
        </div>
      )}

      {/* ── Price Filter ── */}
      <div style={{ borderBottom: '1px solid rgba(196,94,42,0.08)', paddingBottom: '16px', marginBottom: '16px' }}>
        <button onClick={() => setPriceOpen(!priceOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IndianRupee style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Price
          </span>
          {priceOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {priceOpen && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button onClick={() => setPriceRange(null)} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
              borderRadius: '10px', cursor: 'pointer', border: 'none', textAlign: 'left',
              background: priceRange === null ? 'rgba(196,94,42,0.08)' : 'transparent',
            }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '50%',
                border: priceRange === null ? '5px solid #C45E2A' : '2px solid #C4B5A5',
                background: '#fff', flexShrink: 0,
              }} />
              <span style={{ fontSize: '0.85rem', color: priceRange === null ? '#C45E2A' : '#4A3728', fontWeight: priceRange === null ? 600 : 400 }}>
                All Prices
              </span>
            </button>
            {PRICE_RANGES.map((range, i) => {
              const isActive = priceRange && priceRange.min === range.min && priceRange.max === range.max;
              return (
                <button key={i} onClick={() => setPriceRange(range)} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                  borderRadius: '10px', cursor: 'pointer', border: 'none', textAlign: 'left',
                  background: isActive ? 'rgba(196,94,42,0.08)' : 'transparent',
                }}>
                  <div style={{
                    width: '16px', height: '16px', borderRadius: '50%',
                    border: isActive ? '5px solid #C45E2A' : '2px solid #C4B5A5',
                    background: '#fff', flexShrink: 0,
                  }} />
                  <span style={{ fontSize: '0.85rem', color: isActive ? '#C45E2A' : '#4A3728', fontWeight: isActive ? 600 : 400 }}>
                    {range.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Duration Filter ── */}
      <div style={{ borderBottom: '1px solid rgba(196,94,42,0.08)', paddingBottom: '16px', marginBottom: '16px' }}>
        <button onClick={() => setDurationOpen(!durationOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Timer style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Duration
          </span>
          {durationOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {durationOpen && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button onClick={() => setDurationRange(null)} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
              borderRadius: '10px', cursor: 'pointer', border: 'none', textAlign: 'left',
              background: durationRange === null ? 'rgba(196,94,42,0.08)' : 'transparent',
            }}>
              <div style={{
                width: '16px', height: '16px', borderRadius: '50%',
                border: durationRange === null ? '5px solid #C45E2A' : '2px solid #C4B5A5',
                background: '#fff', flexShrink: 0,
              }} />
              <span style={{ fontSize: '0.85rem', color: durationRange === null ? '#C45E2A' : '#4A3728', fontWeight: durationRange === null ? 600 : 400 }}>
                Any Duration
              </span>
            </button>
            {DURATION_RANGES.map((range, i) => {
              const isActive = durationRange && durationRange.min === range.min && durationRange.max === range.max;
              return (
                <button key={i} onClick={() => setDurationRange(range)} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                  borderRadius: '10px', cursor: 'pointer', border: 'none', textAlign: 'left',
                  background: isActive ? 'rgba(196,94,42,0.08)' : 'transparent',
                }}>
                  <div style={{
                    width: '16px', height: '16px', borderRadius: '50%',
                    border: isActive ? '5px solid #C45E2A' : '2px solid #C4B5A5',
                    background: '#fff', flexShrink: 0,
                  }} />
                  <span style={{ fontSize: '0.85rem', color: isActive ? '#C45E2A' : '#4A3728', fontWeight: isActive ? 600 : 400 }}>
                    {range.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Sort ── */}
      <div>
        <button onClick={() => setSortOpen(!sortOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowUpDown style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Sort By
          </span>
          {sortOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {sortOpen && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {SORT_OPTIONS.map(opt => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                borderRadius: '10px', cursor: 'pointer',
                background: sortBy === opt.value ? 'rgba(196,94,42,0.08)' : 'transparent',
              }}>
                <input type="radio" name="service-sort" checked={sortBy === opt.value} onChange={() => setSortBy(opt.value)}
                  style={{ accentColor: '#C45E2A', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '0.85rem', color: sortBy === opt.value ? '#C45E2A' : '#4A3728', fontWeight: sortBy === opt.value ? 600 : 400 }}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-cream"><div className="spinner"></div></div>;

  return (
    <div className="min-h-screen bg-cream" data-testid="services-page">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1C1209 0%, #3A2510 100%)',
        padding: '48px 0 40px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-80px', right: '-80px',
          width: '300px', height: '300px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,140,63,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }} className="text-center">
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(255,248,240,0.08)', padding: '6px 16px', borderRadius: '100px',
            marginBottom: '16px',
          }}>
            <Scissors style={{ width: '14px', height: '14px', color: '#E88C3F' }} />
            <span className="font-body" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#E88C3F', fontWeight: 500 }}>Grooming Services</span>
          </div>
          <h1 className="font-heading" style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800,
            color: '#FFF8F0', marginBottom: '8px',
          }}>Professional Cat Grooming</h1>
          <p className="font-body" style={{ fontSize: '0.95rem', color: 'rgba(255,248,240,0.5)', maxWidth: '500px', margin: '0 auto' }}>
            Expert grooming services to keep your cat looking fabulous. Book an appointment today!
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Top bar: search + mobile filter button */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#8B6A52' }} />
            <Input
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services..."
              className="pl-10 bg-white border-border-custom h-11 font-body" data-testid="service-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
              }}>
                <X style={{ width: '16px', height: '16px', color: '#8B6A52' }} />
              </button>
            )}
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden" style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '0 20px', height: '44px', borderRadius: '100px',
            background: sidebarOpen ? '#C45E2A' : '#fff',
            color: sidebarOpen ? '#fff' : '#4A3728',
            border: sidebarOpen ? 'none' : '1px solid rgba(196,94,42,0.15)',
            cursor: 'pointer', fontWeight: 500, fontSize: '0.88rem',
          }} data-testid="service-filter-toggle">
            <Filter style={{ width: '16px', height: '16px' }} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: sidebarOpen ? 'rgba(255,255,255,0.3)' : '#C45E2A',
                color: '#fff', width: '20px', height: '20px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700,
              }}>{activeFilterCount}</span>
            )}
          </button>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {searchQuery && (
              <span className="font-body" style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
              }}>
                Search: "{searchQuery}"
                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <X style={{ width: '12px', height: '12px' }} />
                </button>
              </span>
            )}
            {priceRange && (
              <span className="font-body" style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
              }}>
                Price: {priceRange.label}
                <button onClick={() => setPriceRange(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <X style={{ width: '12px', height: '12px' }} />
                </button>
              </span>
            )}
            {durationRange && (
              <span className="font-body" style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
              }}>
                Duration: {durationRange.label}
                <button onClick={() => setDurationRange(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <X style={{ width: '12px', height: '12px' }} />
                </button>
              </span>
            )}
            {sortBy !== 'default' && (
              <span className="font-body" style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
              }}>
                Sort: {SORT_OPTIONS.find(o => o.value === sortBy)?.label}
                <button onClick={() => setSortBy('default')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <X style={{ width: '12px', height: '12px' }} />
                </button>
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          {/* ── Desktop Sidebar ── */}
          <aside className="hidden lg:block" style={{
            width: '230px', flexShrink: 0,
            background: '#fff', borderRadius: '20px',
            border: '1px solid rgba(196,94,42,0.08)',
            padding: '20px 18px',
            position: 'sticky', top: '82px',
            maxHeight: 'calc(100vh - 100px)', overflowY: 'auto',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
          }} data-testid="service-desktop-filters">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
              <SlidersHorizontal style={{ width: '16px', height: '16px', color: '#C45E2A' }} />
              <span className="font-heading" style={{ fontSize: '1rem', fontWeight: 700, color: '#1C1209' }}>Filters</span>
            </div>
            <FilterPanel />
          </aside>

          {/* ── Mobile Sidebar Overlay ── */}
          {sidebarOpen && (
            <>
              <div onClick={() => setSidebarOpen(false)} className="lg:hidden" style={{
                position: 'fixed', inset: 0, background: 'rgba(28,18,9,0.4)',
                zIndex: 40, backdropFilter: 'blur(4px)',
              }} />
              <div className="lg:hidden" style={{
                position: 'fixed', left: 0, top: 0, bottom: 0,
                width: '300px', maxWidth: '85vw',
                background: '#fff', zIndex: 50,
                padding: '20px', overflowY: 'auto',
                boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
                animation: 'slideInService 0.3s ease',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SlidersHorizontal style={{ width: '16px', height: '16px', color: '#C45E2A' }} />
                    <span className="font-heading" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1C1209' }}>Filters</span>
                  </div>
                  <button onClick={() => setSidebarOpen(false)} style={{
                    background: '#F5E8D8', border: 'none', borderRadius: '50%',
                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                    <X style={{ width: '16px', height: '16px', color: '#1C1209' }} />
                  </button>
                </div>
                <FilterPanel />
              </div>
            </>
          )}

          {/* ── Service Cards ── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Results summary */}
            <p className="font-body" style={{ fontSize: '0.85rem', color: '#8B6A52', marginBottom: '16px' }}>
              {error ? 'Error loading services' : `${filteredServices.length} service${filteredServices.length !== 1 ? 's' : ''} found`}
            </p>

            {error ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px', color: '#dc2626' }}>⚠️</div>
                <p className="font-body" style={{ fontSize: '1.1rem', color: '#8B6A52', marginBottom: '8px' }}>Failed to load services</p>
                <p className="font-body" style={{ fontSize: '0.85rem', color: '#C4B5A5', marginBottom: '20px' }}>{error}</p>
                <Button onClick={() => window.location.reload()} variant="outline" className="font-body border-rust text-rust rounded-full">Retry</Button>
              </div>
            ) : filteredServices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✂️</div>
                <p className="font-body" style={{ fontSize: '1.1rem', color: '#8B6A52', marginBottom: '8px' }}>No services match your filters</p>
                <p className="font-body" style={{ fontSize: '0.85rem', color: '#C4B5A5', marginBottom: '20px' }}>Try adjusting your search or filters</p>
                <Button onClick={clearAllFilters} variant="outline" className="font-body border-rust text-rust rounded-full">Clear All Filters</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredServices.map(s => (
                  <div key={s.id} className="bg-white rounded-2xl border border-border-custom overflow-hidden group hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid={`service-${s.id}`}>
                    {s.image_urls?.[0] && (
                      <div className="relative h-48 overflow-hidden">
                        <img src={s.image_urls[0]} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        {/* Price badge */}
                        <div style={{
                          position: 'absolute', top: '12px', left: '12px',
                          background: 'rgba(28,18,9,0.85)', backdropFilter: 'blur(8px)',
                          color: '#FFF8F0', padding: '5px 14px', borderRadius: '100px',
                          fontSize: '0.85rem', fontWeight: 700,
                          fontFamily: "'DM Sans', sans-serif",
                          fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
                        }}>
                          ₹{s.price}
                        </div>
                        {/* Duration badge */}
                        <div style={{
                          position: 'absolute', top: '12px', right: '12px',
                          background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
                          color: '#4A3728', padding: '5px 12px', borderRadius: '100px',
                          fontSize: '0.75rem', fontWeight: 500,
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          <Clock style={{ width: '12px', height: '12px' }} /> {s.duration_minutes} min
                        </div>
                        {(s.image_urls.length > 1 || s.video_urls?.length > 0) && (
                          <button onClick={(e) => { e.stopPropagation(); setMediaOpen(s); }} className="font-body" style={{
                            position: 'absolute', bottom: '12px', right: '12px',
                            background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
                            borderRadius: '100px', padding: '5px 14px', fontSize: '0.75rem',
                            fontWeight: 500, color: '#1C1209',
                            display: 'flex', alignItems: 'center', gap: '6px',
                            border: 'none', cursor: 'pointer',
                          }}>
                            {s.video_urls?.length > 0 ? <><Play style={{ width: '12px', height: '12px' }} /> Media</> : <><Image style={{ width: '12px', height: '12px' }} /> {s.image_urls.length} Photos</>}
                          </button>
                        )}
                      </div>
                    )}
                    <div style={{ padding: '18px 20px' }}>
                      <h3 className="font-body" style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1C1209', marginBottom: '6px' }}>{s.name}</h3>
                      <p className="font-body" style={{ fontSize: '0.85rem', color: '#8B6A52', marginBottom: '16px', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{s.description}</p>
                      <div className="flex gap-2">
                        <Button onClick={() => openBooking(s)} className="flex-1 bg-rust hover:bg-rust/90 text-cream font-body rounded-full relative z-10" data-testid={`book-service-${s.id}`}>
                          <Calendar style={{ width: '16px', height: '16px', marginRight: '8px' }} /> Book
                        </Button>
                        <Button onClick={() => openReviewModal(s)} variant="outline" className="flex-1 font-body border-border-custom text-mid-brown hover:text-rust rounded-full relative z-10">
                          <Star className="w-4 h-4 mr-2" /> Reviews
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* My Appointments */}
            {myAppointments.length > 0 && (
              <div style={{ marginTop: '56px' }}>
                <h2 className="font-heading" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1C1209', marginBottom: '20px' }}>My Appointments</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {myAppointments.map(a => (
                    <div key={a.id} style={{
                      background: '#fff', borderRadius: '16px', border: '1px solid rgba(196,94,42,0.08)',
                      padding: '16px 20px', display: 'flex', flexDirection: 'row',
                      justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                      flexWrap: 'wrap',
                    }} data-testid={`appointment-${a.id}`}>
                      <div>
                        <div className="font-body" style={{ fontWeight: 600, color: '#1C1209', marginBottom: '4px' }}>{a.service_name}</div>
                        <div className="font-body" style={{ fontSize: '0.82rem', color: '#8B6A52' }}>
                          {a.date} &middot; {a.start_time} – {a.end_time}
                        </div>
                        {a.pet_name && <div className="font-body" style={{ fontSize: '0.82rem', color: '#4A3728', marginTop: '2px' }}>Pet: {a.pet_name}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="font-body" style={{ fontWeight: 700, color: '#C45E2A', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>₹{a.price}</span>
                        <span className="font-body" style={{
                          padding: '4px 14px', borderRadius: '100px', fontSize: '0.75rem', fontWeight: 600,
                          background: a.status === 'confirmed' ? '#DEF7EC' : a.status === 'completed' ? '#DBEAFE' : '#FEE2E2',
                          color: a.status === 'confirmed' ? '#03543F' : a.status === 'completed' ? '#1E40AF' : '#991B1B',
                        }}>{a.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-md bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">Book: {selectedService?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex items-center gap-3 bg-pale rounded-lg p-3">
              <span className="font-body text-lg font-bold text-rust" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'\u20B9'}{selectedService?.price}</span>
              <span className="font-body text-xs text-mid-brown"><Clock className="w-3 h-3 inline mr-1" />{selectedService?.duration_minutes} min</span>
            </div>
            <div><Label className="font-body text-sm">Select Date</Label>
              <Input type="date" value={selectedDate} min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                onChange={e => { fetchSlots(selectedService.id, e.target.value); setSelectedSlot(null); }}
                className="mt-1 bg-cream/50" data-testid="booking-date" /></div>
            {slotsLoading ? (
              <div className="flex justify-center py-4"><div className="spinner"></div></div>
            ) : slots.length > 0 ? (
              <div><Label className="font-body text-sm mb-2 block">Available Slots</Label>
                <div className="grid grid-cols-3 gap-2">
                  {slots.map(sl => (
                    <button key={sl.id} onClick={() => setSelectedSlot(sl)}
                      className={`px-3 py-2.5 rounded-lg text-xs font-body font-medium transition-all ${selectedSlot?.id === sl.id ? 'bg-rust text-cream shadow-md' : 'bg-pale text-mid-brown hover:bg-rust/10 hover:text-rust'}`}
                      data-testid={`slot-${sl.id}`}>{sl.start_time} - {sl.end_time}</button>
                  ))}
                </div>
              </div>
            ) : selectedDate ? (
              <div className="flex items-center gap-3 bg-amber/10 border border-amber/30 rounded-lg p-4">
                <AlertCircle className="w-5 h-5 text-amber shrink-0" />
                <div>
                  <p className="font-body text-sm text-dark-brown font-medium">No slots available</p>
                  <p className="font-body text-xs text-light-brown">No time slots for this date. Try a different date or contact us.</p>
                </div>
              </div>
            ) : null}
            <div><Label className="font-body text-sm">Pet Name</Label><Input value={petName} onChange={e => setPetName(e.target.value)} className="mt-1 bg-cream/50" placeholder="Your cat's name" data-testid="booking-pet-name" /></div>
            <div><Label className="font-body text-sm">Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 bg-cream/50" placeholder="Any special requests" /></div>
            <Button onClick={handleBook} disabled={!selectedSlot || bookingLoading}
              className={`w-full font-body rounded-full h-11 ${selectedSlot ? 'bg-rust hover:bg-rust/90 text-cream' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              data-testid="confirm-booking-btn">
              {bookingLoading ? 'Booking...' : selectedSlot ? 'Confirm Booking' : 'Select a time slot to book'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Dialog */}
      <Dialog open={!!mediaOpen} onOpenChange={() => setMediaOpen(null)}>
        <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">{mediaOpen?.name} - Gallery</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {mediaOpen?.image_urls?.map((url, i) => <img key={i} src={url} alt="" className="w-full rounded-xl" />)}
            {mediaOpen?.video_urls?.map((url, i) => <video key={i} src={url} controls className="w-full rounded-xl" />)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewModalOpen} onOpenChange={() => setReviewModalOpen(false)}>
        <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">Reviews: {selectedService?.name}</DialogTitle></DialogHeader>
          
          <div className="mt-4">
            {reviewsLoading ? (
               <div className="flex justify-center p-6"><div className="spinner"></div></div>
            ) : (
              <div className="space-y-4 mb-8">
                {reviews.length === 0 ? (
                  <p className="font-body text-light-brown text-center py-4">No reviews yet. Be the first!</p>
                ) : (
                  reviews.map(r => (
                    <div key={r.id} className="bg-cream/30 p-3 rounded-lg border border-border-custom">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-body font-bold text-dark-brown text-sm">{r.user_name}</span>
                        <span className="text-xs text-light-brown">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex mb-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < r.rating ? 'fill-rust text-rust' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      {r.comment && <p className="font-body text-xs text-mid-brown mt-1">{r.comment}</p>}
                    </div>
                  ))
                )}
              </div>
            )}

            <div className="bg-cream/20 p-4 rounded-xl border border-border-custom">
              <h3 className="font-heading text-md font-bold text-dark-brown mb-3">Write a Review</h3>
              {user ? (
                <form onSubmit={submitReview}>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-mid-brown mb-1">Rating</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button type="button" key={star} onClick={() => setReviewRating(star)} className="focus:outline-none">
                          <Star className={`w-6 h-6 ${star <= reviewRating ? 'fill-rust text-rust' : 'text-gray-300 hover:text-rust/50'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-mid-brown mb-1">Comment (Optional)</label>
                    <textarea
                      value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                      className="w-full border border-border-custom rounded-lg p-2 text-sm focus:outline-none focus:border-rust"
                      rows="3" placeholder="Share your experience..."
                    />
                  </div>
                  <Button type="submit" disabled={reviewLoading} className="w-full bg-rust hover:bg-dark-brown text-cream text-sm rounded-full h-9">
                    {reviewLoading ? 'Submitting...' : 'Submit Review'}
                  </Button>
                  <p className="text-[10px] text-light-brown text-center mt-2">You can submit up to 3 reviews per service.</p>
                </form>
              ) : (
                <div className="text-center py-4">
                  <p className="font-body text-sm text-mid-brown mb-3">Please log in to write a review.</p>
                  <Button onClick={() => navigate('/login')} size="sm" className="bg-dark-brown text-cream rounded-full">Log In</Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile sidebar animation */}
      <style>{`
        @keyframes slideInService {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
